import {
  AthleteAttendancePeriodItem,
  AthleteAttendanceTeam,
  AthleteAttendanceWeekItem,
} from '../../models/attendance.models';
import { formatPeriodDateRangeLabel } from '../period-summary/period-summary.helpers';

const FALLBACK_THEMES = [
  { cardBg: '#EFF6FF', avatarBg: '#1E40AF' },
  { cardBg: '#ECFDF5', avatarBg: '#166534' },
  { cardBg: '#FFFBEB', avatarBg: '#B45309' },
  { cardBg: '#F5F3FF', avatarBg: '#6D28D9' },
];

const RING_COLOR = '#2563EB';
const RING_TRACK = '#F2F2F7';

export interface TeamCardTheme {
  cardBg: string;
  avatarBg: string;
  ringColor: string;
  ringTrack: string;
}

export interface PeriodChartRow {
  label: string;
  periodIndex: number;
  [teamKey: string]: string | number;
}

const hexToRgba = (hex: string, alpha: number): string => {
  const normalized = hex.replace('#', '');
  if (normalized.length !== 6) {
    return `rgba(37, 99, 235, ${alpha})`;
  }
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const colorToRgba = (color: string, alpha: number): string => {
  const rgbMatch = color.match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/i);
  if (rgbMatch) {
    return `rgba(${rgbMatch[1]}, ${rgbMatch[2]}, ${rgbMatch[3]}, ${alpha})`;
  }
  if (color.startsWith('#')) {
    return hexToRgba(color, alpha);
  }
  return `rgba(37, 99, 235, ${alpha})`;
};

export const parseAttendanceResponse = (data: unknown): AthleteAttendanceTeam[] => {
  if (Array.isArray(data)) {
    return data as AthleteAttendanceTeam[];
  }
  return [];
};

export const getTeamInitials = (name?: string): string =>
  name
    ? name
        .split(' ')
        .map((w) => w[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    : '?';

export const getTeamCardTheme = (color: string | undefined, index: number): TeamCardTheme => {
  const fallback = FALLBACK_THEMES[index % FALLBACK_THEMES.length];
  const avatarBg = color || fallback.avatarBg;
  const cardBg = color ? colorToRgba(color, 0.08) : fallback.cardBg;

  return {
    cardBg,
    avatarBg,
    ringColor: RING_COLOR,
    ringTrack: RING_TRACK,
  };
};

export const formatAttendancePercent = (percent: number): string => `${Math.round(percent)}%`;

export const hasAttendanceData = (teams: AthleteAttendanceTeam[] | null | undefined): boolean =>
  (teams?.length ?? 0) > 0;

export const createEmptyPeriodAttendance = (periodIndex: number): AthleteAttendancePeriodItem => ({
  index: periodIndex,
  period: '',
  practicePercent: 0,
  attended: 0,
  markedAttended: 0,
});

export const getTeamPeriodAttendanceOrDefault = (
  team: AthleteAttendanceTeam,
  periodIndex: number
): AthleteAttendancePeriodItem =>
  getTeamPeriodAttendance(team, periodIndex) ?? createEmptyPeriodAttendance(periodIndex);

export const getTeamPeriodAttendance = (
  team: AthleteAttendanceTeam,
  periodIndex: number
): AthleteAttendancePeriodItem | undefined => team.periodBreakdown.find((item) => item.index === periodIndex);

export const createEmptyWeekAttendance = (weekIndex: number): AthleteAttendanceWeekItem => ({
  index: weekIndex,
  week: '',
  period: 0,
  practicePercent: 0,
  attended: 0,
  markedAttended: 0,
});

export const getTeamWeekAttendance = (
  team: AthleteAttendanceTeam,
  weekIndex: number
): AthleteAttendanceWeekItem | undefined => (team.weekBreakdown ?? []).find((item) => item.index === weekIndex);

export const getTeamWeekAttendanceOrDefault = (
  team: AthleteAttendanceTeam,
  weekIndex: number
): AthleteAttendanceWeekItem => getTeamWeekAttendance(team, weekIndex) ?? createEmptyWeekAttendance(weekIndex);

export const hasWeekAttendanceData = (entry: AthleteAttendanceWeekItem | undefined): boolean =>
  (entry?.markedAttended ?? 0) > 0;

export const hasPeriodAttendanceData = (entry: AthleteAttendancePeriodItem | undefined): boolean =>
  (entry?.markedAttended ?? 0) > 0;

export const hasSeasonAttendanceData = (team: AthleteAttendanceTeam): boolean => (team.totalMarkedAttended ?? 0) > 0;

export const getRingDashArray = (percent: number, radius = 19.5): string => {
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(100, percent));
  const filled = (clamped / 100) * circumference;
  return `${filled} ${circumference - filled}`;
};

export const buildPeriodChartData = (teams: AthleteAttendanceTeam[]): PeriodChartRow[] => {
  const periodMap = new Map<number, { period: string; values: Record<string, number> }>();

  teams.forEach((team) => {
    team.periodBreakdown.forEach((item) => {
      if (!periodMap.has(item.index)) {
        periodMap.set(item.index, { period: item.period, values: {} });
      }
      const entry = periodMap.get(item.index);
      entry.values[String(team.teamId)] = item.practicePercent;
    });
  });

  return Array.from(periodMap.entries())
    .sort(([a], [b]) => a - b)
    .map(([periodIndex, { period, values }]) => ({
      label: formatPeriodDateRangeLabel(period),
      periodIndex,
      ...values,
    }));
};

export const getTeamLineColor = (team: AthleteAttendanceTeam, index: number): string => {
  if (team.color) {
    return team.color;
  }
  return FALLBACK_THEMES[index % FALLBACK_THEMES.length].avatarBg;
};
